/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import React, { useEffect, useRef } from "react";
import PropTypes from "prop-types";

import {
  FilterPill,
  Icon,
  caratIcon,
  closeIcon,
  filterIcon,
  Drawer,
  Button,
  Checkbox
} from "@lululemon/dsp-ecom-pattern-library";
import classNames from "classnames/bind";
import { WaeIcon } from "@lululemon/dsp-legacy-shared";
import { trackEvent } from "@lululemon/ecom-core";
import styles from "./store-locator-filter.module.scss";
import { useAmenities } from "../../util/amenities";
import { useAwayHandler } from "../../util/hooks";

const cx = classNames.bind(styles);

const areSimilarObjects = (firstObject, secondObject) => {
  return (
    firstObject.length === secondObject.length &&
    firstObject.every((v, i) => v === secondObject[i])
  );
};

const StoreLocatorFilter = ({ onFilterChanged, isMobileView, isModalView }) => {
  const allAmenities = useAmenities();

  const [filterDrawerisOpen, setFilterDrawerisOpen] = React.useState(false);
  const [selectedAmenities, setSelectedAmenities] = React.useState([]);
  const [unappliedSelectedAmenities, setUnappliedSelectedAmenities] =
    React.useState([]);

  const trackSearchfilters = ({ name, data }) => {
    trackEvent(name, {
      appliedFilters: data
    });
  };

  const toggleFilterItems = (name) => {
    if (selectedAmenities.indexOf(name) === -1) {
      setSelectedAmenities([...selectedAmenities, name]);
      if (!isMobileView) onFilterChanged([...selectedAmenities, name]);
      trackSearchfilters({
        name: "filterApplied",
        data: [...selectedAmenities, name]
      });
    } else {
      const updatedItems = selectedAmenities.filter((val) => val !== name);
      setSelectedAmenities(updatedItems);
      if (!isMobileView) onFilterChanged(updatedItems);
      trackSearchfilters({ name: "filterApplied", data: updatedItems });
    }
  };

  const applyFilters = () => {
    onFilterChanged(selectedAmenities);
    setFilterDrawerisOpen(false);
    setUnappliedSelectedAmenities(selectedAmenities);
  };

  useEffect(() => {
    const close = (e) => {
      if (e.keyCode === 27) {
        setFilterDrawerisOpen(false);
      }
    };
    window.addEventListener("keydown", close);
    return () => window.removeEventListener("keydown", close);
  }, []);

  const desktopButtonRef = useRef();
  const containerRef = useRef();
  useAwayHandler([containerRef, desktopButtonRef], () => {
    if (isMobileView) return;

    setFilterDrawerisOpen(false);
  });

  const filterWrapperContent = () => {
    return (
      <div
        ref={containerRef}
        role="menu"
        className={cx(styles.storeLocatorFilterWrapper, {
          elevated: isModalView
        })}
      >
        {isMobileView && selectedAmenities.length > 0 && (
          <div role="menuitem" className={styles.selectedFilterContainer}>
            {selectedAmenities.map((value) => (
              <FilterPill
                icon={closeIcon}
                kind="filter"
                tag="a"
                href="#"
                onClick={() => toggleFilterItems(value)}
                className={styles.selectedAmenities}
                key={`amenitie-${Math.random().toFixed(3)}`}
              >
                {allAmenities.filter((data) => data.id === value)[0].name}
              </FilterPill>
            ))}
          </div>
        )}
        <div className={styles.storeLocatorFilterTitle}>
          Services and Amenities
        </div>
        {allAmenities.map((data) => {
          return (
            <div className={styles.storeLocatorFilterRow} key={data.id}>
              <Checkbox
                id={data.id}
                name={data.name}
                classes={{
                  icon: styles.checkBox,
                  label: "checkboxDemoLabel",
                  labelContent: styles.checkBoxContent
                }}
                checked={selectedAmenities.indexOf(data.id) !== -1}
                onChange={(event) => {
                  toggleFilterItems(event.target.id);
                }}
                data-testid="filter_checkbox"
              >
                <WaeIcon
                  name={data.icon}
                  width="16px"
                  height="16px"
                  color="#000000"
                  className={styles.amenitiesIcon}
                />
                <span className={styles.amenitiesText}>{data.name}</span>
              </Checkbox>
            </div>
          );
        })}
      </div>
    );
  };

  let numOfAmenitiesText = "";
  if (selectedAmenities.length) {
    numOfAmenitiesText = `(${selectedAmenities.length})`;
  }

  return (
    <div className={styles.storeLocatorFilterContainer}>
      {isMobileView ? (
        <>
          <button
            onClick={() => {
              setFilterDrawerisOpen(!filterDrawerisOpen);
              trackEvent("storeLocatorFilterviewed");
            }}
            data-testid="mobileFilterBtn"
            type="button"
            className={cx(styles.filterInputWrapper, {
              [styles.filterInputWrapperMobile]: isMobileView
            })}
          >
            <Icon
              content={filterIcon}
              className={styles.filterIcon}
              title="filterIcon"
            />
            {numOfAmenitiesText && <span>{numOfAmenitiesText}</span>}
          </button>
          <Drawer
            visible={!!filterDrawerisOpen}
            placement="left"
            messages={{ close: "Close" }}
            onRequestClose={() => {
              if (
                !areSimilarObjects(
                  selectedAmenities,
                  unappliedSelectedAmenities
                )
              ) {
                setSelectedAmenities(unappliedSelectedAmenities);
              }
              setFilterDrawerisOpen(false);
            }}
            width="80%"
            classes={{
              container: styles.drawerOuterContainer
            }}
          >
            {filterWrapperContent()}
            <div className={styles.applyButtonContainer}>
              <Button
                key={`apply_${numOfAmenitiesText}`}
                mobileBlock
                variant="filled"
                kind="secondary"
                onClick={applyFilters}
                data-test-id="apply-filters"
                className={styles.applyButton}
              >
                Apply {numOfAmenitiesText}
              </Button>
            </div>
          </Drawer>
        </>
      ) : (
        <>
          <button
            ref={desktopButtonRef}
            data-testid="filterBtn"
            onClick={() => {
              setFilterDrawerisOpen(!filterDrawerisOpen);
              trackEvent("storeLocatorFilterviewed");
            }}
            type="button"
            className={styles.filterInputWrapper}
            aria-label="Filter"
            aria-expanded={filterDrawerisOpen}
          >
            Filter {numOfAmenitiesText}
            <Icon
              content={caratIcon}
              className={cx(styles.caratIcon, {
                caratIconUp: filterDrawerisOpen
              })}
            />
          </button>
          {filterDrawerisOpen && filterWrapperContent()}
        </>
      )}
    </div>
  );
};

StoreLocatorFilter.propTypes = {
  onFilterChanged: PropTypes.func,
  isMobileView: PropTypes.bool,
  isModalView: PropTypes.bool
};

StoreLocatorFilter.defaultProps = {
  onFilterChanged: () => {},
  isMobileView: false,
  isModalView: false
};

export default StoreLocatorFilter;
